import { Box, MenuItem } from '@material-ui/core';
import { apiActions } from 'app/core/api/api-redux';
import {
  currencyCodes,
  mapCurrencyCodeToSymbol,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import {
  useCreateRepairEvent,
  useCreateRepairImageEvent,
  useRepairerCase,
} from 'app/core/api/repairer/api-repairer';
import {
  CurrencyObject,
  EventEntity,
  nonActionMsgTypes,
  quotingMsgTypes,
  SendChatData,
} from 'app/core/api/repairer/api-repairer.types';
import { defaultEventSendType } from 'app/core/api/user/user-utils';
import { MissingItemError } from 'app/core/errors/error-variants';
import SelectInput from 'app/shared/components/form-input/SelectInput';
import TextInput from 'app/shared/components/form-input/TextInput';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import MultiPartInputBox from 'app/shared/frames/MultiPartInputBox';
import { isInArray, Opt } from 'app/tools/general.types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { EMPTY, switchMap } from 'rxjs';
import { getFullMessageDetails } from './chat-msg-templates';

export type InitiateMsg = (chatData: SendChatData) => Promise<void>;

type GetQuoteFromDialog = (title: string) => Promise<CurrencyObject>;

export const EMPTY_MESSAGE = '<empty message>';

const DialogMessage: React.VFC<{
  modifiableCurrencyObject: Partial<CurrencyObject>;
}> = ({ modifiableCurrencyObject }) => (
  <Box display="flex" flexDirection="column">
    <Body>Enter your quote below</Body>
    <Box height="10px" />
    <MultiPartInputBox variant="flush" gridCols="6em 1fr">
      <SelectInput
        defaultValue="GBP"
        minHeight={45}
        onChange={e => {
          // eslint-disable-next-line no-param-reassign
          modifiableCurrencyObject.currency = String(e.target.value);
        }}
      >
        {_.keys(currencyCodes).map(code => (
          <MenuItem value={code} key={code}>
            {mapCurrencyCodeToSymbol.get(code)}
          </MenuItem>
        ))}
      </SelectInput>
      <TextInput
        greyPlaceholder
        minHeight={45}
        placeholder="0.00"
        label=""
        type="number"
        onChange={e => {
          // eslint-disable-next-line no-param-reassign
          modifiableCurrencyObject.amount = Number(e.target.value);
        }}
      />
    </MultiPartInputBox>
  </Box>
);

const useGetQuoteFromDialog = (): GetQuoteFromDialog => {
  const { showDialog, showSnack } = useShowInfo();
  return (title: string) =>
    new Promise<CurrencyObject>(
      (resolveWithQuote, rejectGetInput) => {
        const modifiableCurrencyObject = {
          currency: 'GBP',
        } as Partial<CurrencyObject>;

        const dialogClickSendCallback = (): Opt<boolean> => {
          const { amount = 0, currency } = modifiableCurrencyObject;
          if (!currency) throw new MissingItemError('currency code');
          if (amount <= 0 || amount?.toFixed(0).length > 8) {
            showSnack({
              message: 'Error. Please enter a valid quote amount',
              alertType: 'error',
            });
            return false; // notifies dialog to not close, as invalid
          }
          // returns data to the useGetInputFromDialog caller
          resolveWithQuote({
            currency,
            amount,
          });
          // no 'false' return = dialog can close happily
        };

        showDialog({
          title,
          onDismissed: rejectGetInput, // reject data acquisition if dismissed
          message: (
            <DialogMessage {...{ modifiableCurrencyObject }} />
          ),
          actions: [
            { text: 'Send Quote', callback: dialogClickSendCallback },
            { text: 'Cancel' }, // default action is dismiss dialog
          ],
        });
      }
    );
};

/**
 * Hook that returns the initiateMessage function. This will send a message
 * if it is a normal use type message, or if it is a dialog prompt type
 * message (sending a quote), it will trigger the dialog to show, and
 * return the a promise that resolves once the message is sent, or rejects
 * if the dialog is dismissed.
 * @param id the case id in which to send the message
 * @param cancelRefresh the callback to stop any current chat refresh loading
 * @returns a function you can call to initiate a message sending, which completes
 * its returned promise only if the message sent - otherwise it errors.
 */
export const useInitiateMsg = (
  id: string,
  cancelRefresh: () => void
): InitiateMsg => {
  const dispatch = useDispatch();
  const { showSnack } = useShowInfo();
  const { request$: createRepairEvent$ } = useCreateRepairEvent();
  const { request$: createRepairImageEvent$ } =
    useCreateRepairImageEvent();

  const getQuoteFromDialog = useGetQuoteFromDialog();
  const { resource: repairCase, request: refreshRepairCase } =
    useRepairerCase(id);

  const { events: chatEvents = [] } = repairCase || {};

  /**
   * Inserts the message into the stream straight away (in a paler color
   * format to denote unverified status). Temporary message is overwritten
   * when updated chat history data comes in from the refresh triggered.
   
   * We can do this for quick action types too, because of the action-bar clearing
   * on each action use, and only updating when a *different* list of actions
   * comes back from the API.
   * TODO: this only works as long the quick actions always changes after each use!
   * Ideally we need a message send ID to detect when the send message appears on
   * chat events list from the API.
   */
  const addEventsImmediately = (eventData: SendChatData[]) => {
    if (!repairCase) return;
    const tempEvents: EventEntity[] = eventData.map(
      ({ displayText, eventType, image }) => ({
        displayText: image ? EMPTY_MESSAGE : displayText,
        eventType: eventType || defaultEventSendType,
        // negative id denotes temporary message to be displayed in pale format
        id: _.random(-9999999, -1000000, false),
        repairCase: repairCase.id,
        imageUrl: image ? URL.createObjectURL(image) : undefined,
      })
    );
    // cancel any refreshes in progress that would overwrite the temporary message
    cancelRefresh();
    dispatch(
      apiActions.repairCase.set({
        ...repairCase,
        events: [...chatEvents, ...tempEvents],
      })
    );
  };

  /** initiateMsg actual function */
  return async ({
    eventType = defaultEventSendType,
    displayText: displayTextInput,
    image: imageIn = null,
  }: SendChatData) => {
    let quotationAmount: Opt<CurrencyObject>;
    let messageContentText = '';
    let image =
      imageIn &&
      new File([imageIn], imageIn.name, _.pick(imageIn, 'type'));

    // NOTE: this sets the file to null if a non-standard event was
    // sent such as a quick action
    if (image && eventType !== defaultEventSendType) {
      image = null;
    }

    if (isInArray(eventType, quotingMsgTypes)) {
      // if getInputFromDialog rejects, the initiateMsg function will reject
      // as well. This is the desired behavior, indicating message didn't send.
      quotationAmount = await getQuoteFromDialog(displayTextInput);
    }

    if (isInArray(eventType, nonActionMsgTypes)) {
      messageContentText = displayTextInput;
    }

    const { messageText, actionData } = getFullMessageDetails({
      quotationAmount,
      eventType,
    });

    const sendChatData = {
      displayText: messageContentText || messageText || EMPTY_MESSAGE,
      eventType,
      actionData,
    };

    const caseIdObj = { repairCase: +id };
    const eventHasText = sendChatData.displayText !== EMPTY_MESSAGE;

    const messageSentObserver = {
      next: refreshRepairCase,
      error: () =>
        showSnack({
          message: 'Something went wrong! Please try again.',
        }),
    };

    if (image) {
      const sendChatImageData = {
        displayText: EMPTY_MESSAGE,
        eventType,
        image,
      };
      addEventsImmediately(
        [sendChatImageData, eventHasText ? sendChatData : []].flat()
      );
      /** NOTE: is an EMPTY observable if no text to send (),
       * otherwise is the send-text-event observable */
      const secondEventIfText$ = eventHasText
        ? createRepairEvent$({ ...sendChatData, ...caseIdObj })
        : EMPTY;
      createRepairImageEvent$({ ...sendChatImageData, ...caseIdObj })
        .pipe(switchMap(() => secondEventIfText$))
        .subscribe(messageSentObserver);
    } else {
      addEventsImmediately([sendChatData]);
      createRepairEvent$({ ...caseIdObj, ...sendChatData }).subscribe(
        messageSentObserver
      );
    }
    // non-error return completes promise, indicating message was sent
  };
};
